// =============================================================================
// Day panel — the list of one day's entries. Two shapes, same list:
//
// * Sheet (phones, below md): slides up from the bottom, full viewport
//   height with a narrow width (centered horizontally), so it covers the
//   calendar without going edge-to-edge; drag down (from the top of the
//   list) to dismiss.
// * Inline column (`inline`, from md — iPad, iPhone landscape, desktop):
//   a bordered column beside the month grid, as tall as the grid and
//   sticky, so the grid stays visible and tapping another day just swaps
//   the list. No backdrop, no drag, no body-scroll lock.
//
// Tapping an event hands it to Calendar.js for the detail view; birthdays,
// holidays and to-dos are read-only here.
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Gift, CalendarDays, CheckSquare } from 'lucide-react';
import { priorityEmoji } from '../../lib/priorities';
import { formatDate as fmt } from '../../lib/dates';
import { useTranslation } from '../../lib/i18n';

const DayPanel = ({
  date,
  items,
  getCategory,
  isSharedView,
  renderCreatorAvatar,
  onClose,
  onAddEvent,
  onOpenEvent,
  inline = false
}) => {
  const { t } = useTranslation();
  // Drag-to-dismiss offset for the day modal (px). Positive = dragged down.
  const [dayModalDy, setDayModalDy] = useState(0);
  const [dayModalAnim, setDayModalAnim] = useState(false);
  const dayModalDragRef = useRef(null);
  // Ref to the inner scrollable list inside the day modal. We only engage
  // the swipe-to-close gesture when this list is scrolled to the top.
  const dayModalScrollRef = useRef(null);

  // Lock body scroll while the day modal is open. Without this, iOS
  // rubber-band scrolling lets the page underneath drift around while the
  // user drags the modal. We use the `position: fixed` trick because
  // `overflow: hidden` alone is not enough to stop overscroll on iOS.
  // The inline column covers nothing, so the page keeps scrolling there.
  useEffect(() => {
    if (!date || inline) return undefined;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [date, inline]);

  const header = (
    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
      <h3 className="font-semibold text-gray-900 truncate">
        {fmt(date, 'full')}
      </h3>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onAddEvent(date)}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 px-2 py-1"
        >
          <Plus size={16} /> {t('calendar.event')}
        </button>
        <button
          onClick={() => onClose()}
          className="p-1 hover:bg-gray-100 rounded"
          aria-label={t('common.close')}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );

  const list = items.length === 0 ? (
    <div className="flex-1 flex items-center justify-center px-4 py-8 text-center text-gray-500 text-sm">
      {t('calendar.nothingPlanned')}
    </div>
  ) : (
    <div ref={dayModalScrollRef} className="divide-y divide-gray-100 px-4 overflow-y-auto flex-1">
      {items.map((ev) => {
        const cat = getCategory(ev.categoryId);
        return (
          <div
            key={ev.id}
            onClick={() => {
              if (!ev.isBirthday && !ev.isTodo && !ev.isHoliday) {
                // Open the detail view (read-only, with comments
                // and history). Edit is reachable from there.
                onOpenEvent(ev);
              }
            }}
            className={`flex items-stretch gap-3 py-3 ${
              ev.isBirthday || ev.isTodo || ev.isHoliday ? 'cursor-default' : 'hover:bg-gray-50 cursor-pointer'
            }`}
          >
            <div className="flex flex-col items-end justify-center w-14 flex-shrink-0 text-xs font-medium leading-tight">
              {ev.isBirthday ? (
                <Gift size={18} style={{ color: cat.color }} />
              ) : ev.isHoliday ? (
                <CalendarDays size={18} style={{ color: cat.color }} />
              ) : ev.isTodo ? (
                <>
                  <CheckSquare size={18} style={{ color: cat.color }} />
                  {ev.startTime && (
                    <span className="text-gray-900 mt-0.5">{ev.startTime}</span>
                  )}
                </>
              ) : ev.allDay ? (
                <span className="text-gray-500">{t('calendar.allDay')}</span>
              ) : (
                <>
                  <span className="text-gray-900">{ev.startTime || '—'}</span>
                  {ev.endTime && (
                    <span className="text-gray-400 mt-0.5">{ev.endTime}</span>
                  )}
                </>
              )}
            </div>
            <div
              className="w-1 rounded-full flex-shrink-0"
              style={{ backgroundColor: cat.color }}
            />
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="flex items-center gap-1.5 min-w-0">
                <div
                  className={`font-semibold truncate flex-1 min-w-0 ${
                    ev.isTodo && ev.done
                      ? 'line-through text-gray-400'
                      : 'text-gray-900'
                  }`}
                >
                  {ev.title}
                </div>
                {priorityEmoji(ev.priority) && (
                  <span className="flex-shrink-0 leading-none pr-0.5">
                    {priorityEmoji(ev.priority)}
                  </span>
                )}
              </div>
              {ev.notes && (
                <div className="text-xs text-gray-500 truncate mt-0.5">{ev.notes}</div>
              )}
            </div>
            {isSharedView && !ev.isBirthday && (() => {
              // Faces belong to participants only: an event nobody was
              // added to shows none, whoever created it.
              const ids = Array.isArray(ev.participantIds) ? ev.participantIds : [];
              if (ids.length === 0) return null;
              return (
                <div className="flex items-center flex-shrink-0 -space-x-2">
                  {ids.slice(0, 4).map((uid) => (
                    <div key={uid} className="ring-2 ring-white dark:ring-gray-900 rounded-full">
                      {renderCreatorAvatar(uid, 28)}
                    </div>
                  ))}
                  {ids.length > 4 && (
                    <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
                      +{ids.length - 4}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );

  if (inline) {
    return (
      // The outer div is a flex sibling of the month grid and stretches to
      // its height; the inner card is sticky and capped at the viewport,
      // so on a short screen (iPhone landscape) it stays put while the grid
      // scrolls and the list scrolls inside it. Narrower at md than at lg:
      // beside the 5rem rail an iPad in portrait has ~640px for both, and a
      // 20rem column would squeeze the grid below phone width.
      <div className="w-64 lg:w-80 flex-shrink-0">
        <div
          className="sticky h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
            maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 1.5rem)'
          }}
        >
          {header}
          {list}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40"
      onClick={() => onClose()}
    >
      <div
        // Leave the status bar clear: a full 100vh sheet slid its own
        // header up behind the clock and the blur strip.
        className={`bg-white w-full max-w-md h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] sm:h-[90vh] sm:my-4 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
          dayModalDy === 0 && !dayModalAnim ? 'animate-slide-up' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translateY(${dayModalDy}px)`,
          transition: dayModalAnim ? 'transform 220ms ease-out' : 'none',
          willChange: 'transform'
        }}
        onTouchStart={(e) => {
          // Record the start of a potential drag. We'll only engage
          // it on the first move if the inner list is at the top
          // and the user is moving downward — otherwise we leave
          // the touch alone so normal scrolling can happen.
          const t = e.touches[0];
          dayModalDragRef.current = {
            y: t.clientY,
            startScrollTop: dayModalScrollRef.current?.scrollTop || 0,
            mode: 'pending'
          };
          setDayModalAnim(false);
        }}
        onTouchMove={(e) => {
          const s = dayModalDragRef.current;
          if (!s) return;
          const dy = e.touches[0].clientY - s.y;
          if (s.mode === 'pending') {
            // Only first significant movement decides the gesture.
            if (Math.abs(dy) < 5) return;
            if (dy > 0 && s.startScrollTop <= 0) {
              s.mode = 'dragging';
            } else {
              s.mode = 'scrolling';
            }
          }
          if (s.mode === 'dragging') {
            setDayModalDy(Math.max(0, dy));
          }
        }}
        onTouchEnd={() => {
          const s = dayModalDragRef.current;
          dayModalDragRef.current = null;
          if (!s || s.mode !== 'dragging') return;
          const dy = dayModalDy;
          setDayModalAnim(true);
          if (dy > 120) {
            // Animate off-screen, then close.
            setDayModalDy(window.innerHeight);
            setTimeout(() => {
              onClose();
              setDayModalDy(0);
              setDayModalAnim(false);
            }, 220);
          } else {
            setDayModalDy(0);
            setTimeout(() => setDayModalAnim(false), 220);
          }
        }}
      >
        {/* Drag handle — visual affordance only; the entire panel is
            draggable via the touch handlers above. */}
        <div className="flex-shrink-0 pt-2 pb-1 flex justify-center">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>
        {header}
        {list}
      </div>
    </div>
  );
};

export default DayPanel;
